import type {
  ActionProposal,
  AnswerAction,
  AnswerSource,
  ChatMessage,
  Locale,
  OpenContext,
  OutgoingPayload,
  ApusAssistantApi,
  PanelSize,
  Quota,
  Role,
  SessionState,
  ThemeMode,
  Attachment,
  TraceStep,
  TransportChunk,
  WidgetEvent,
  WidgetEventMap,
  WidgetOptions,
} from "./types.ts";
import { DEFAULTS, normalizeTokens, requestHeaders, type ResolvedConfig } from "./core/config.ts";
import { Emitter } from "./core/emitter.ts";
import { formatResetTime, speechLang, strings, type Strings } from "./core/i18n.ts";
import { Session } from "./core/session.ts";
import { Transport, TransportError, normalizeProposals } from "./core/transport.ts";
import { createRecorderVoice, createVoice, recorderSupported, type Voice } from "./core/voice.ts";
import { assignDefined, clamp, toError } from "./core/util.ts";
import styles from "./ui/styles.css";
import { View, type PendingAttachment } from "./ui/view.ts";
import { downloadFile } from "./core/download.ts";
import { feedbackBody, type FeedbackCategory } from "./core/feedback.ts";
import { Hub } from "./hub/hub.ts";

/* esbuild thay thế định danh này lúc build bằng version trong package.json.
   `typeof` trên định danh chưa khai báo là an toàn, không ném — nên chạy thẳng
   nguồn (test, công cụ) vẫn được, chỉ là báo "dev". */
declare const __QXC_VERSION__: string;
export const VERSION: string = typeof __QXC_VERSION__ === "string" ? __QXC_VERSION__ : "dev";

export class Assistant implements ApusAssistantApi {
  readonly version = VERSION;

  private config: ResolvedConfig;
  private strings: Strings;
  private emitter: Emitter;
  private session: Session;
  private transport: Transport;
  private view: View;
  private voice: Voice;
  /** Chế độ Hub (toàn màn hình) — chỉ có khi gọi qua server (apiBase). */
  private hub: Hub | null = null;
  /** Tệp đang chờ gửi kèm câu hỏi tiếp theo; `sending` = id tệp của lượt đang chạy. */
  private pending: Array<PendingAttachment & { file?: Attachment }> = [];
  /** Bản demo: token của vai đang chọn trong widget (đè getToken của trang chủ). */
  private demoToken: string | null = null;
  private downloading = false;
  /** Vừa "Cuộc trò chuyện mới" / đổi vai: phiên tiếp theo không mở lại hội thoại cũ. */
  private fresh = false;
  /** Mỗi request hỏi token ở đây: vai demo đang chọn, nếu không thì getToken của trang chủ. */
  private tokenSource = (): string | null | Promise<string | null> => this.demoToken ?? (this.config.getToken ? this.config.getToken() : null);
  private sending: string[] = [];

  private opened = false;
  private destroyed = false;
  private booted = false;
  private busy = false;
  private controller: AbortController | null = null;
  private sessionController: AbortController | null = null;
  private autoOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private themeQuery: MediaQueryList | null = null;
  private onThemeChange: (() => void) | null = null;
  private onKeydown: ((event: KeyboardEvent) => void) | null = null;
  private mobileQuery: MediaQueryList | null = null;

  private context: OpenContext | null = null;
  /** Kích thước panel hiện tại; `sizeChosen` = người dùng đã tự chọn (thì không tự mở rộng nữa). */
  private size: PanelSize = "compact";
  private sizeChosen = false;
  /** Phần tử đang giữ focus lúc mở panel — thường là nút Ask của app chủ. */
  private restoreFocus: HTMLElement | null = null;
  private quota: Quota | null = null;
  private remote: SessionState = {};
  private voiceBase = "";
  /** Đã chuyển sang ghi âm + nhận dạng ở server (phiên báo `voice.server`). */
  private serverVoice = false;
  /** Bản demo: server bật nút "Xoá dữ liệu demo" (vẽ ở thanh trên của Hub). */
  private demoResetOn = false;
  /** Khoá nào app chủ đã khai — server không ghi đè lên. */
  private explicit = new Set<string>();

  constructor(options: WidgetOptions = {}) {
    this.config = resolve(options);
    this.strings = strings(this.config.locale);
    this.emitter = new Emitter((error) => this.log("listener error", error));
    this.context = this.config.context;

    this.session = new Session({
      persist: this.config.persistSession,
      limit: clamp(this.config.historyLimit, 10, 1000),
      scope: this.config.client,
    });

    this.transport = new Transport({
      apiBase: this.config.apiBase,
      chatPath: this.config.endpoints.chat,
      sessionPath: this.config.endpoints.session,
      headers: requestHeaders(this.config),
      credentials: this.config.credentials,
      timeout: Math.max(0, this.config.timeout),
      getToken: this.tokenSource,
      streamProtocol: this.config.streamProtocol,
      backend: this.config.backend,
    });

    this.view = new View({
      styles,
      strings: this.strings,
      markdown: this.config.markdown,
      maxLength: this.config.maxLength,
      callbacks: {
        onSubmit: (text) => void this.send(text),
        onStop: () => this.stop(),
        onToggle: () => this.toggle(),
        onClose: () => this.close(),
        onReset: () => this.reset(),
        onSize: (size) => this.setSize(size, true),
        onMicToggle: () => this.voice.toggle(),
        onPromptPick: (text) => {
          /* Bấm gợi ý là gửi luôn — mục 6.2: "bấm một câu gợi ý để nhận trả
             lời: tính một lượt, như câu gõ tay". Điền vào ô rồi bắt bấm tiếp
             là thêm một bước thừa. */
          void this.send(text);
        },
        onAction: (action, messageId) => this.handleAction(action, messageId),
        onRetry: (id) => this.retryFrom(id),
        onProposal: (messageId, proposalId, decision, note, edits) => void this.decideProposal(messageId, proposalId, decision, note, edits),
        onProposalEdit: (messageId, proposalId, edits) => void this.editProposal(messageId, proposalId, edits),
        onTool: (messageId, tool, feedback) => void this.messageTool(messageId, tool, feedback),
        onAttach: (files) => this.attachFiles(files),
        onRole: (token) => this.switchRole(token),
        onPreviewFile: (file) => void this.previewFile(file),
        onRemoveAttachment: (key) => {
          this.pending = this.pending.filter((x) => x.key !== key);
          this.view.renderPending(this.pending);
        },
      },
    });

    const server = this.transport.hasServer();
    this.view.setServerTools(server);
    this.view.setAttachEnabled(server);
    if (server && this.config.mode !== "inline") {
      this.hub = new Hub({
        api: (method, path, body) => this.transport.api(method, path, body),
        view: this.view,
        messages: () => this.session.messages,
        conversationId: () => this.session.conversationId,
        openConversation: (id, focusIndex) => this.openConversation(id, focusIndex),
        newConversation: () => this.reset(),
        setSize: (size) => this.setSize(size, true),
        close: () => this.close(),
        send: (text) => void this.send(text),
        context: () => this.context,
        openRoleMenu: (anchor) => this.view.toggleRoleMenu(anchor),
        download: (url, name) => this.download(url, name),
        previewPdf: (url, name) => this.previewUrl(url, name),
        canDemoReset: () => this.demoResetOn,
        demoReset: () => void this.demoReset(),
      });
    }

    this.voice = createVoice(speechLang(this.config.locale), {
      onStart: () => {
        this.voiceBase = this.view.refs.input.value.trim();
        if (this.voiceBase) this.voiceBase += " ";
      },
      onResult: (text) => this.view.setInput(this.voiceBase + text),
      onStateChange: (recording) => this.view.setRecording(recording),
      onError: () => {
        this.view.setRecording(false);
        this.view.announce(this.strings.speechDenied);
      },
    });

    this.applyConfigToView();
    this.view.setVoiceAvailable(this.config.voice && this.voice.supported);

    /* kích thước: lựa chọn của người dùng trong tab này thắng cấu hình mặc định */
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem("apus:size");
    } catch {
      saved = null;
    }
    const start = saved === "compact" || saved === "wide" || saved === "full" ? saved : this.config.size;
    this.sizeChosen = !!saved;
    this.setSize(start, false);
  }

  /* ------------------------------------------------------------------ boot */

  mount(): this {
    if (this.booted || this.destroyed) return this;
    this.booted = true;

    if (this.config.mode === "inline") this.mountInline();
    else document.body.appendChild(this.view.host);

    this.view.renderAll(this.session.messages);
    this.refreshIntro();
    this.watchTheme();
    this.watchViewport();

    this.onKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && this.opened && this.config.mode !== "inline") this.close();
    };
    document.addEventListener("keydown", this.onKeydown);

    if (this.config.mode === "inline") {
      this.opened = true;
      this.syncModal();
      this.view.setOpen(true);
      if (this.config.fetchSession) void this.refreshSession();
    } else if (this.config.autoOpen !== false) {
      const delay = typeof this.config.autoOpen === "number" ? this.config.autoOpen : 0;
      this.autoOpenTimer = setTimeout(() => this.open(), Math.max(0, delay));
    }

    this.emitter.emit("ready", { widget: this });
    this.config.onReady?.(this);
    return this;
  }

  private mountInline(): void {
    const target = this.config.target;
    const node =
      typeof target === "string"
        ? document.querySelector<HTMLElement>(target)
        : target instanceof HTMLElement
          ? target
          : null;

    if (!node) {
      this.warn('mode "inline" nhưng không tìm thấy target — quay về chế độ dock.');
      this.config.mode = "dock";
      this.view.setMode("dock");
      document.body.appendChild(this.view.host);
      return;
    }
    node.appendChild(this.view.host);
  }

  /* --------------------------------------------------------------- session */

  /**
   * Tóm tắt + ba gợi ý + hạn mức. Gọi lúc mở panel, và gọi lại khi app chủ báo
   * user đổi board. Không tính lượt: server dựng sẵn, không gọi mô hình.
   */
  async refreshSession(): Promise<void> {
    if (!this.config.fetchSession || this.destroyed) return;
    this.sessionController?.abort();
    const controller = new AbortController();
    this.sessionController = controller;

    try {
      const remote = await this.transport.fetchSession(this.context, controller.signal, this.session.conversationId, this.fresh && !this.session.conversationId);
      if (this.destroyed || controller.signal.aborted) return;
      this.remote = remote || {};
      this.applyRemote(this.remote);
      this.emitter.emit("session:loaded", { session: this.remote });
    } catch (error) {
      if (controller.signal.aborted) return;
      const err = toError(error);
      if (err instanceof TransportError && err.isAuth) {
        this.emitter.emit("auth:error", { status: err.status ?? 401 });
        this.view.announce(this.strings.authError);
      }
      /* Tóm tắt hỏng không được làm chết panel: user vẫn gõ câu hỏi được. */
      this.log("không tải được phiên", error);
      this.emitter.emit("error", { error: err, phase: "session" });
    }
  }

  private applyRemote(remote: SessionState): void {
    const take = <K extends keyof SessionState>(key: K): SessionState[K] | undefined =>
      this.explicit.has(key as string) ? undefined : remote[key];

    assignDefined(this.config, {
      title: take("title"),
      subtitle: take("subtitle"),
      placeholder: take("placeholder"),
      avatarUrl: take("avatarUrl"),
      theme: take("theme"),
    } as Partial<ResolvedConfig>);

    const remoteTokens = take("tokens");
    if (remoteTokens) this.config.tokens = { ...normalizeTokens(remoteTokens), ...this.config.tokens };

    if (remote.locale && !this.explicit.has("locale")) this.setLocale(remote.locale);

    if (remote.demo && Array.isArray(remote.demo.roles)) {
      this.view.setDemoRoles(remote.demo.roles, this.demoToken ?? remote.demo.current ?? null);
    }

    if (remote.quota) this.setQuota(remote.quota);

    if (remote.voice?.server) this.useServerVoice();
    const demoReset = remote.demoReset === true && this.transport.hasServer();
    if (demoReset !== this.demoResetOn) {
      this.demoResetOn = demoReset;
      this.hub?.onMessagesChanged();
    }

    if (Array.isArray(remote.messages) && !this.busy) {
      /* server Hub có nhiều hội thoại: nhận đúng hội thoại mà danh sách tin này thuộc về */
      if (remote.conversationId && remote.conversationId !== this.session.conversationId) this.session.conversationId = remote.conversationId;
      this.session.replace(remote.messages.map((m) => (m && m.proposals ? { ...m, proposals: normalizeProposals(m.proposals) } : m)));
      this.view.renderAll(this.session.messages);
      this.hub?.onMessagesChanged();
    }

    this.applyConfigToView();
    this.refreshIntro();

    if (remote.disabled) {
      this.view.setBlocked(remote.disabledMessage || this.strings.networkError);
    } else {
      this.syncBlocked();
    }
  }

  /** Server nhận dạng giọng nói ⇒ thay Web Speech bằng ghi âm gửi server: chạy cả Firefox, tiếng Việt + từ vựng ngành. */
  private useServerVoice(): void {
    if (this.serverVoice || this.destroyed || !this.transport.hasServer() || !recorderSupported()) return;
    this.serverVoice = true;
    this.voice.destroy();
    this.voice = createRecorderVoice({
      onStart: () => {
        this.voiceBase = this.view.refs.input.value.trim();
        if (this.voiceBase) this.voiceBase += " ";
      },
      onResult: (text) => {
        this.view.setInput(this.voiceBase + text);
        /* nói xong là gửi (owner 2026-10-21): câu hiện thành tin nhắn của người dùng; lệnh GHI vào ERP vẫn qua
           bước xác nhận đề xuất như khi gõ. `voiceAutoSend: false` ⇒ chỉ điền vào ô nhập để xem lại. */
        if (this.config.voiceAutoSend !== false) this.view.submitInput();
        else this.view.refs.input.focus();
      },
      onStateChange: (recording) => this.view.setRecording(recording),
      onPhase: (phase) => this.view.setVoicePhase(phase),
      onError: (code) => {
        this.view.setRecording(false);
        this.view.setVoicePhase("idle");
        if (code === "not-allowed") this.view.voiceNotice(this.strings.speechDenied);
        else if (code.startsWith("server:")) this.view.voiceNotice(code.slice(7));
        else this.view.voiceNotice(this.strings.speechFailed);
      },
      transcribe: (audio) => this.transport.transcribe(audio),
    });
    this.view.setVoiceAvailable(this.config.voice && this.voice.supported);
  }

  private applyConfigToView(): void {
    const c = this.config;
    this.view.setMode(c.mode);
    this.view.setAnchor(c.anchor);
    this.view.setMarkdown(c.markdown);
    this.view.setLauncher(c.launcher && c.mode !== "inline");
    this.view.setMaxLength(c.maxLength);
    this.view.setHeader({
      title: c.title || this.strings.title,
      subtitle: c.subtitle ?? this.strings.subtitle,
      placeholder: c.placeholder || this.strings.placeholder,
      avatarUrl: c.avatarUrl,
    });
    this.view.setMascot(c.mascotUrl);
    this.view.setTokens({
      ...c.tokens,
      "--apx-width": c.width + "px",
      "--apx-header-h": c.headerHeight + "px",
    });
    this.view.host.style.zIndex = String(c.zIndex);
    this.resolveTheme();
  }

  private refreshIntro(): void {
    this.view.renderIntro({
      summary: this.remote.summary,
      prompts: this.remote.prompts,
      actions: this.remote.actions,
      /* Ẩn khi hội thoại đã bắt đầu — gợi ý là thứ để mở đầu. */
      hidden: this.session.messages.some((m) => m.role === "user"),
    });
  }

  /* ----------------------------------------------------------------- theme */

  private watchTheme(): void {
    if (this.themeQuery || typeof window.matchMedia !== "function") return;
    this.themeQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.onThemeChange = () => {
      if (this.config.theme === "auto") this.resolveTheme();
    };
    if (typeof this.themeQuery.addEventListener === "function") {
      this.themeQuery.addEventListener("change", this.onThemeChange);
    }
  }

  private resolveTheme(): void {
    let theme = this.config.theme;
    if (theme === "auto") {
      /* "auto" bám theo app chủ trước, hệ điều hành sau: dashboard đã chọn
         data-theme="dark" thì trợ lý sáng lên giữa nền tối là sai. */
      const pageTheme = document.documentElement.getAttribute("data-theme");
      if (pageTheme === "dark" || pageTheme === "light") theme = pageTheme;
      else theme = window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    this.view.setTheme(theme === "dark" ? "dark" : "light");
  }

  /** Mobile chiếm toàn màn nên bề rộng báo cho app chủ là 0 — app không được
   *  thu hẹp gì cả. Đổi kích thước cửa sổ thì báo lại. */
  private watchViewport(): void {
    if (typeof window.matchMedia !== "function") return;
    this.mobileQuery = window.matchMedia("(max-width: 720px)");
    const handler = () => {
      if (!this.opened) return;
      /* Xoay ngang điện thoại, hoặc kéo cửa sổ qua ngưỡng 720px: panel đổi từ
         che kín sang nằm cạnh, nên vai trò ARIA và bẫy Tab phải đổi theo. */
      this.syncModal();
      this.emitOpen();
    };
    if (typeof this.mobileQuery.addEventListener === "function") {
      this.mobileQuery.addEventListener("change", handler);
    }
  }

  /** Panel che kín màn hình thì mới là modal. Dock trên desktop thì không —
   *  app vẫn dùng được, nhốt Tab ở đó là giam người dùng bàn phím. */
  private syncModal(): void {
    this.view.setModal(this.config.mode !== "inline" && !!this.mobileQuery?.matches);
  }

  getWidth(): number {
    /* float nổi đè lên trang, inline nằm trong hộp của app: cả hai không chiếm dải
       bên phải nên app không được thu hẹp nội dung. Chỉ dock mới báo bề rộng. */
    if (!this.opened || this.config.mode !== "dock") return 0;
    if (this.mobileQuery?.matches) return 0;
    if (this.size === "full") return 0; /* toàn màn hình nổi đè, app không phải chừa chỗ */
    return this.size === "wide" ? Math.max(this.config.width, 680) : this.config.width;
  }

  getSize(): PanelSize {
    return this.size;
  }

  /** Đổi kích thước panel. `manual` = do người dùng bấm nút (được nhớ trong tab, tắt tự mở rộng). */
  setSize(size: PanelSize, manual = true): void {
    if (size !== "compact" && size !== "wide" && size !== "full") return;
    this.size = size;
    if (manual) {
      this.sizeChosen = true;
      try {
        sessionStorage.setItem("apus:size", size);
      } catch {
        /* storage bị chặn: chỉ nhớ trong phiên chạy */
      }
    }
    this.view.setSize(size);
    if (this.hub) {
      /* điện thoại: panel vốn đã toàn màn hình, không đủ chỗ cho ba cột — giữ khung chat thường */
      if (size === "full" && !this.mobileQuery?.matches) this.hub.activate();
      else this.hub.deactivate();
    }
    if (this.config.mode === "dock") {
      this.view.setTokens({ "--apx-width": (size === "wide" ? Math.max(this.config.width, 680) : this.config.width) + "px" });
      if (this.opened) this.emitOpen();
    }
  }

  /* ------------------------------------------------------------------ chat */

  async send(text: string): Promise<void> {
    const message = String(text ?? "").trim().slice(0, this.config.maxLength);
    if (!message || this.destroyed) return;
    /* Tối đa một câu đang xử lý. View đã khoá ô nhập, đây là chốt thứ hai cho
       các lệnh gọi từ app chủ. */
    if (this.busy) return;

    if (this.pending.some((x) => x.status === "uploading")) {
      this.view.announce("Đợi tệp tải lên xong rồi gửi");
      this.hub?.toast("Đợi tệp tải lên xong rồi gửi");
      return;
    }
    const files = this.pending.filter((x) => x.status === "ready" && x.file).map((x) => x.file as Attachment);
    this.sending = files.map((f) => f.id);
    this.pending = [];
    this.view.renderPending(this.pending);

    this.emitter.emit("send", { text: message });
    const userMessage = this.session.add("user", message, files.length ? { attachments: files } : undefined);
    this.view.appendMessage(userMessage);
    this.emitMessage(userMessage);
    this.refreshIntro();

    await this.run(message);
  }

  private async run(text: string): Promise<void> {
    const controller = new AbortController();
    this.controller = controller;
    this.busy = true;

    this.view.setPending(true);
    this.view.setBusy(true);

    let assistant: ChatMessage | null = null;
    let buffer = "";
    let failure: { message: string; code?: string } | null = null;
    let sources: AnswerSource[] | null = null;
    let actions: AnswerAction[] | null = null;
    let proposals: ActionProposal[] | null = null;
    let created: Attachment[] | null = null;
    let trace: TraceStep[] | null = null;
    let intent: { kind: string; label: string } | null = null;
    let followUps: string[] | null = null;
    let sampleData = false;

    const emit = (chunk: TransportChunk): void => {
      switch (chunk.type) {
        case "conversation":
          this.fresh = false;
          if (this.session.conversationId !== chunk.conversationId) {
            this.session.conversationId = chunk.conversationId;
            this.session.save();
            this.emitter.emit("conversation:start", { conversationId: chunk.conversationId });
          }
          break;
        case "delta":
          buffer += chunk.text;
          assistant = this.ensureAssistant(assistant, buffer);
          break;
        case "message":
          buffer = chunk.text;
          assistant = this.ensureAssistant(assistant, buffer);
          break;
        case "progress":
          if (!assistant) this.view.setPending(true, chunk.label || this.strings.progress(chunk.stage));
          break;
        case "sources":
          sources = chunk.sources;
          break;
        case "actions":
          actions = chunk.actions;
          break;
        case "sample":
          sampleData = chunk.sampleData;
          break;
        case "quota":
          this.setQuota(chunk.quota);
          break;
        case "prompts":
          this.remote.prompts = chunk.prompts;
          followUps = chunk.prompts;
          break;
        case "proposals":
          proposals = chunk.proposals;
          break;
        case "files":
          created = chunk.files;
          break;
        case "trace":
          trace = chunk.trace;
          break;
        case "intent":
          intent = chunk.intent;
          break;
        case "error":
          failure = chunk;
          break;
        case "done":
          break;
      }
    };

    try {
      await this.dispatch(this.payload(text), controller.signal, emit);

      if (failure) throw new TransportError((failure as { message: string }).message, undefined, (failure as { code?: string }).code);
      if (!buffer.trim()) throw new TransportError(this.strings.emptyError);

      const finished = this.finishAssistant(assistant, buffer, { sources, actions, sampleData, proposals, created, trace, intent, followUps });
      /* biểu đồ, bảng: tự mở rộng một lần nếu người dùng chưa tự chọn kích thước */
      if (finished && this.config.autoExpand && !this.sizeChosen && this.size === "compact" && this.config.mode !== "inline" && !this.mobileQuery?.matches && (/```(chart|html|htm|svg)\b/.test(buffer) || /^\|.*\|\s*$/m.test(buffer))) {
        this.setSize("wide", false);
      }
      if (finished) {
        this.emitter.emit("reply", { message: finished });
        this.emitMessage(finished);
        this.view.announce(finished.text.slice(0, 200));
      }
      this.refreshIntro();
      this.hub?.onMessagesChanged();
    } catch (error) {
      if (controller.signal.aborted) {
        /* Người dùng bấm Dừng: phần chữ đã nhận được vẫn giữ nguyên. */
        this.finishAssistant(assistant, buffer, { sources, actions, sampleData });
      } else {
        this.showError(toError(error), assistant);
      }
    } finally {
      this.sending = [];
      if (this.controller === controller) this.controller = null;
      this.busy = false;
      this.view.setPending(false);
      this.view.setBusy(false);
      this.syncBlocked();
    }
  }

  /** streaming "auto": thử SSE trước; nếu hỏng mà chưa nhận được chữ nào thì
   *  thử lại một lần bằng JSON thường. */
  private async dispatch(
    payload: OutgoingPayload,
    signal: AbortSignal,
    emit: (chunk: TransportChunk) => void,
  ): Promise<void> {
    const canStream = typeof ReadableStream !== "undefined";
    const mode = this.config.streaming;
    const wantStream = mode === false ? false : canStream;

    if (!wantStream) {
      await this.transport.send({ ...payload, stream: false }, signal, emit, false);
      return;
    }

    let received = false;
    const watch = (chunk: TransportChunk): void => {
      if (chunk.type === "delta" || chunk.type === "message") received = true;
      emit(chunk);
    };

    try {
      await this.transport.send(payload, signal, watch, true);
    } catch (error) {
      /* Lỗi xác thực và lỗi nghiệp vụ không phải lý do để thử lại — chỉ rơi
         về JSON khi nghi ngờ đường SSE hỏng. */
      const err = error as TransportError;
      const worthRetry = mode === "auto" && !received && !signal.aborted && !(err?.status && err.status < 500);
      if (!worthRetry) throw error;
      this.log("SSE hỏng, thử lại bằng JSON", error);
      await this.transport.send({ ...payload, stream: false }, signal, emit, false);
    }
  }

  /** Danh tính KHÔNG nằm ở đây. Vai và quyền CoS do server suy ra từ JWT —
   *  client khai được là giả mạo được. */
  private payload(text: string): OutgoingPayload {
    const payload: OutgoingPayload = {
      conversationId: this.session.conversationId,
      message: text,
      stream: this.config.streaming !== false,
      locale: this.config.locale,
    };
    if (this.context) payload.context = this.context;
    if (this.config.metadata) payload.metadata = this.config.metadata;
    if (this.sending.length) payload.attachments = this.sending;
    return payload;
  }

  private ensureAssistant(current: ChatMessage | null, text: string): ChatMessage {
    if (current) {
      this.session.update(current.id, { text });
      current.text = text;
      this.view.updateMessage(current);
      return current;
    }
    /* Chữ đầu tiên về tới nơi: bỏ ba chấm, thay bằng bong bóng thật. */
    this.view.setPending(false);
    const message = this.session.add("assistant", text, { streaming: true });
    this.view.appendMessage(message);
    return message;
  }

  private finishAssistant(
    message: ChatMessage | null,
    text: string,
    extra: { sources: AnswerSource[] | null; actions: AnswerAction[] | null; sampleData: boolean; proposals?: ActionProposal[] | null; created?: Attachment[] | null; trace?: TraceStep[] | null; intent?: { kind: string; label: string } | null; followUps?: string[] | null },
  ): ChatMessage | null {
    if (!message) return null;
    const patch: Partial<ChatMessage> = { text, streaming: false, sampleData: extra.sampleData };
    if (extra.sources) patch.sources = extra.sources;
    if (extra.actions) patch.actions = extra.actions;
    if (extra.proposals) patch.proposals = extra.proposals;
    if (extra.created && extra.created.length) patch.attachments = extra.created;
    if (extra.trace) patch.trace = extra.trace;
    if (extra.intent) patch.intent = extra.intent;
    if (extra.followUps && extra.followUps.length) patch.followUps = extra.followUps.slice(0, 3);
    const updated = this.session.update(message.id, patch);
    this.view.updateMessage(updated || message);
    return updated || message;
  }

  private showError(error: Error, partial: ChatMessage | null): void {
    const text = this.friendly(error);
    this.log("chat error", error);

    if (error instanceof TransportError && error.isAuth) {
      this.emitter.emit("auth:error", { status: error.status ?? 401 });
    }

    if (partial && partial.text.trim()) {
      /* Đã trả lời được một nửa rồi mới đứt: giữ phần đã có, báo lỗi riêng. */
      this.finishAssistant(partial, partial.text, { sources: null, actions: null, sampleData: false });
    } else if (partial) {
      this.session.remove(partial.id);
      this.view.removeMessage(partial.id);
    }

    /* bản dùng thử của mã khách đã tắt/hết hạn (server demo, clients.json): khoá ô nhập, khỏi gõ tiếp vô ích */
    if (error instanceof TransportError && error.isClientGate) this.view.setBlocked(error.message);

    const note = this.session.add("assistant", text, { error: true });
    this.view.appendMessage(note);
    this.view.announce(text);
    this.emitter.emit("error", { error, phase: "chat" });
    this.config.onError?.(error);
  }

  private friendly(error: Error): string {
    if (error instanceof TransportError) {
      if (error.message === "timeout") return this.strings.timeoutError;
      if (error.isClientGate) return error.message;
      if (error.partyGate === "no_party_membership") return this.strings.noPartyMembership;
      if (error.partyGate === "active_party_mismatch") return this.strings.activePartyMismatch;
      if (error.isAuth) return this.strings.authError;
    }
    if (error.message === this.strings.emptyError) return this.strings.emptyError;
    return this.strings.networkError;
  }

  private retryFrom(errorMessageId: string): void {
    this.session.remove(errorMessageId);
    this.view.removeMessage(errorMessageId);
    this.retry();
  }

  retry(): void {
    if (this.busy) return;
    const last = this.session.lastQuestion();
    if (last) void this.run(last);
  }

  private handleAction(action: AnswerAction, messageId: string | null): void {
    const message = messageId ? this.session.messages.find((m) => m.id === messageId) || null : null;
    this.emitter.emit("action", { action, message });
    this.config.onAction?.(action);
    /* "Tải PDF": tải thẳng về máy (fetch + blob), KHÔNG mở cửa sổ mới — tránh bị chặn popup */
    /* "PDF": XEM TRƯỚC ngay trong khung chat (có nút Tải về trong khung), ⛔ tải thẳng */
    if (action.type === "link" && /\/export\/[0-9a-f-]{36}\.pdf$/i.test(action.target)) {
      const heading = message ? /^#{1,2}\s+(.+)$/m.exec(message.text)?.[1]?.replace(/[*_`]/g, "").trim() : "";
      void this.previewUrl(action.target, (heading || "Báo cáo Apus Ami").slice(0, 80) + ".pdf");
      return;
    }
    /* link khác (nguồn internet…): mở tab mới ngay trong lượt bấm — trình duyệt không chặn */
    if (action.type === "link" && /^https?:\/\//i.test(action.target)) {
      window.open(action.target, "_blank", "noopener");
      return;
    }
    if (!this.config.onAction && !this.emitterHasAction()) {
      this.warn('có chip bước tiếp theo nhưng chưa ai nghe — truyền onAction hoặc on("action").');
    }
  }

  private emitterHasAction(): boolean {
    return this.emitter.has("action");
  }

  /* ---------------------------------------------------------------- quota */

  private setQuota(quota: Quota): void {
    this.quota = quota;
    this.emitter.emit("quota", { quota });
    this.syncBlocked();
  }

  /** Chạm trần: panel vẫn mở để đọc lại, ô nhập đổi thành thông báo kèm giờ
   *  reset. Chip ACT NOW (nếu có quyền CoS) do server gửi kèm câu trả lời. */
  private syncBlocked(): void {
    if (this.remote.disabled) return;
    const quota = this.quota;
    /* Core trả về tầng hạn mức đang chạm trần trước (ngày hay tháng); `period`
       quyết định cách gọi tên nó. Thiếu thì coi như hạn mức ngày. */
    const period = quota?.period ?? "day";
    if (quota && quota.remaining <= 0) {
      const at = quota.resetAt ? formatResetTime(quota.resetAt, this.config.locale, period) : "";
      this.view.setBlocked(at ? this.strings.quotaSpent(at, period) : this.strings.quotaSpentNoTime(period));
      this.view.setQuotaLabel("");
      return;
    }
    this.view.setBlocked(null);
    this.view.setQuotaLabel(quota ? this.strings.quotaLeft(quota.remaining, period) : "");
  }

  getQuota(): Quota | null {
    return this.quota ? { ...this.quota } : null;
  }

  /* --------------------------------------------------------- API công khai */

  open(context?: OpenContext): void {
    if (this.destroyed) return;
    if (context) this.context = { ...(this.context || {}), ...context };
    if (this.opened) {
      /* Đã mở sẵn mà bấm Ask từ chỗ khác: cập nhật ngữ cảnh, lấy lại gợi ý. */
      if (context && this.config.fetchSession) void this.refreshSession();
      return;
    }
    this.restoreFocus = activeElement();
    this.opened = true;
    this.syncModal();
    this.view.setOpen(true);
    this.view.focusOnOpen();
    this.emitOpen();
    if (this.config.fetchSession) void this.refreshSession();
  }

  private emitOpen(): void {
    const payload = { width: this.getWidth(), context: this.context };
    this.emitter.emit("open", payload);
    this.config.onOpen?.(payload);
  }

  replayMascot(): void {
    this.close();
    this.view.replayMascot();
  }

  close(): void {
    if (!this.opened || this.config.mode === "inline") return;
    this.opened = false;
    this.voice.stop();
    this.view.setOpen(false);

    /* Trả focus về nơi người dùng đứng trước khi mở. Không làm thì focus kẹt
       trên phần tử vừa bị ẩn, trình duyệt đẩy nó về <body>, và người dùng bàn
       phím mất chỗ đứng. */
    const target = this.restoreFocus;
    this.restoreFocus = null;
    if (target && target.isConnected && typeof target.focus === "function") {
      target.focus();
    }

    this.emitter.emit("close", { width: 0 });
    this.config.onClose?.();
  }

  toggle(context?: OpenContext): void {
    if (this.opened) this.close();
    else this.open(context);
  }

  isOpen(): boolean {
    return this.opened;
  }

  push(text: string, role: Role = "assistant"): ChatMessage {
    const message = this.session.add(role, String(text));
    this.view.appendMessage(message);
    this.emitMessage(message);
    return message;
  }

  stop(): void {
    this.controller?.abort();
    this.controller = null;
    this.busy = false;
    this.view.setPending(false);
    this.view.setBusy(false);
  }

  clear(): void {
    this.stop();
    this.session.clear();
    this.view.clearThread();
    this.refreshIntro();
  }

  /** Bản demo: hỏi mật khẩu ⇒ server xoá dữ liệu hỏi đáp thử của kho này ⇒ bắt đầu cuộc trò chuyện mới. */
  private async demoReset(): Promise<void> {
    if (this.busy) return;
    let error: string | undefined;
    for (;;) {
      const password = await this.view.askPassword({
        title: "Xoá dữ liệu demo",
        message: "Xoá TOÀN BỘ dữ liệu hỏi đáp thử (hội thoại, đề xuất, tệp, lịch…) của bản demo này. Không hoàn tác được.",
        confirm: "Xoá dữ liệu",
        error,
      });
      if (!password) return;
      try {
        await this.transport.demoReset(password);
        this.reset();
        this.hub?.onRoleChanged(); /* tải lại danh sách hội thoại, thông báo, số đếm — về tab Trò chuyện */
        this.hub?.toast("Đã xoá dữ liệu demo");
        this.view.announce("Đã xoá dữ liệu demo");
        return;
      } catch (e) {
        const err = toError(e);
        error = err.message || "Không xoá được";
        /* sai mật khẩu ⇒ hỏi lại ngay trong hộp; lỗi khác (khoá tạm, đang chạy tác vụ) ⇒ báo rồi thôi */
        if (!(err instanceof TransportError && err.code === "wrong_password")) {
          this.hub?.toast("Không xoá được: " + error);
          this.view.announce("Không xoá được: " + error);
          return;
        }
      }
    }
  }

  reset(): void {
    this.stop();
    this.sessionController?.abort();
    this.session.reset();
    this.fresh = true;
    this.view.clearThread();
    this.refreshIntro();
    this.view.focusInput();
    if (this.config.fetchSession) void this.clearRemoteSession();
    this.hub?.onMessagesChanged();
  }

  /** Mở một hội thoại đã lưu (Hub). `focusIndex` = vị trí tin nhắn cần cuộn tới (kết quả tìm kiếm). */
  async openConversation(id: string, focusIndex?: number): Promise<void> {
    if (!this.transport.hasServer() || this.busy) return;
    try {
      this.fresh = false;
      const conv = await this.transport.api<{ id: string; messages: ChatMessage[] }>("GET", "/conversations/" + encodeURIComponent(id));
      this.stop();
      this.session.conversationId = conv.id;
      this.session.replace(
        (conv.messages || []).map((m) => ({ ...m, id: String(m.id), proposals: m.proposals ? normalizeProposals(m.proposals) : undefined })),
      );
      this.view.renderAll(this.session.messages);
      this.refreshIntro();
      const target = focusIndex != null ? this.session.messages[focusIndex] : undefined;
      if (target) setTimeout(() => this.view.focusMessage(target.id), 50);
      this.hub?.onMessagesChanged();
    } catch (error) {
      this.view.announce(this.friendly(toError(error)));
    }
  }

  /** Xác nhận / huỷ một đề xuất hành động trên thẻ trong chat. */
  /** Sửa thư trên thẻ ⇒ server kiểm tra lại ⇒ vẽ lại thẻ (cảnh báo/chặn mới). Trả thẻ mới, null nếu lỗi. */
  private async editProposal(messageId: string, proposalId: string, edits: Record<string, string>): Promise<ActionProposal | null> {
    const message = this.session.messages.find((m) => m.id === messageId);
    if (!message) return null;
    try {
      const r = await this.transport.api<{ proposal: unknown }>("PATCH", "/proposals/" + encodeURIComponent(proposalId), edits);
      const updated = normalizeProposals([r.proposal])[0];
      if (!updated) return null;
      const proposals = (message.proposals || []).map((p) => (p.id === proposalId ? updated : p));
      const saved = this.session.update(messageId, { proposals });
      this.view.updateMessage(saved || message);
      return updated;
    } catch (error) {
      this.view.updateMessage(message);
      this.hub?.toast("Không sửa được: " + toError(error).message);
      this.view.announce(toError(error).message);
      return null;
    }
  }

  private async decideProposal(messageId: string, proposalId: string, decision: "confirm" | "cancel", note?: string, edits?: Record<string, string>): Promise<void> {
    const message = this.session.messages.find((m) => m.id === messageId);
    if (!message) return;
    /* còn ô sửa chưa lưu (bấm Xác nhận ngay khi đang gõ) ⇒ lưu + kiểm tra lại trước; bị chặn thì dừng để người dùng xem */
    if (edits && decision === "confirm") {
      const updated = await this.editProposal(messageId, proposalId, edits);
      if (!updated) return;
      if (updated.blocked) {
        this.hub?.toast("Kiểm tra lại thông tin thư — có mục bị chặn");
        this.view.announce("Kiểm tra lại thông tin thư — có mục bị chặn");
        return;
      }
    }
    const current = this.session.messages.find((m) => m.id === messageId) || message;
    try {
      const r = await this.transport.api<{ proposal: unknown }>("POST", "/proposals/" + encodeURIComponent(proposalId) + "/" + decision, note ? { note } : {});
      const updated = normalizeProposals([r.proposal])[0];
      if (updated) {
        const proposals = (current.proposals || []).map((p) => (p.id === proposalId ? updated : p));
        const saved = this.session.update(messageId, { proposals });
        this.view.updateMessage(saved || current);
        this.hub?.toast(
          decision === "cancel" ? "Đã huỷ — không thay đổi gì"
          : updated.state === "awaiting_approval" ? "Đã gửi " + (updated.approverLabel || "người duyệt") + " duyệt"
          : updated.state === "failed" ? "Gửi thất bại: " + (updated.error || "")
          : updated.type === "gui_email" ? "Đã gửi email"
          : "Đã thực hiện (demo): " + (updated.record || ""),
        );
        this.view.announce(updated.type === "gui_email" ? (updated.state === "done" ? "Đã gửi email" : "Gửi email thất bại") : "Đã cập nhật đề xuất");
        this.view.announce(decision === "cancel" ? "Đã huỷ đề xuất" : "Đã xác nhận đề xuất");
      }
      this.hub?.onMessagesChanged();
    } catch (error) {
      this.view.updateMessage(current); /* bật lại nút */
      this.view.announce(this.friendly(toError(error)));
      this.hub?.toast("Không thực hiện được: " + toError(error).message);
    }
  }

  /** Xem trước một tệp đính kèm: Office ⇒ HTML server chuyển; còn lại ⇒ tải bytes (có xác thực) rồi hiện. */
  async previewFile(file: Attachment): Promise<void> {
    const p = this.view.preview;
    p.loading(file.name);
    try {
      if (/officedocument/.test(file.type)) {
        /* bản xem (HTML do server chuyển) + tệp gốc cho nút Tải về; tải tệp gốc lỗi thì vẫn xem được */
        const id = encodeURIComponent(file.id);
        const [r, orig] = await Promise.all([
          this.transport.api<{ html: string }>("GET", "/files/" + id + "/preview"),
          this.transport.blob("/files/" + id).catch(() => undefined),
        ]);
        p.open({ name: file.name, type: file.type, html: r.html, file: orig });
      } else {
        p.open({ name: file.name, type: file.type, blob: await this.transport.blob("/files/" + encodeURIComponent(file.id)) });
      }
    } catch (error) {
      p.error(file.name, "Không mở được tệp: " + toError(error).message);
    }
  }

  /** Xem trước một bản xuất báo cáo: hiện bản HTML (cùng nội dung + giao diện với PDF — ⛔ phụ thuộc trình xem PDF
   *  của trình duyệt, ⛔ chờ dựng PDF); nút Tải về trong khung tải đúng file PDF. */
  async previewUrl(url: string, name: string): Promise<void> {
    const p = this.view.preview;
    p.onDownloadUrl = (u, n) => void this.download(u, n);
    p.loading(name);
    try {
      const html = await (await this.transport.blob(url.replace(/\.pdf$/i, ".html"))).text();
      p.open({ name, type: "text/html", html, downloadUrl: url, downloadName: name.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "").toLowerCase() + ".pdf" });
    } catch (error) {
      p.error(name, "Không mở được báo cáo: " + toError(error).message);
    }
  }

  /** Tải file (PDF) về máy, báo tiến độ — dùng chung cho chip "Tải PDF" và Hub. */
  async download(url: string, name = "apus-ami.pdf"): Promise<void> {
    if (this.downloading) return;
    this.downloading = true;
    this.view.announce("Đang tạo PDF…");
    this.hub?.toast("Đang tạo PDF… (vài giây)");
    try {
      const saved = await downloadFile(url, name);
      this.view.announce("Đã tải " + saved);
      this.hub?.toast("Đã tải " + saved);
    } catch (error) {
      const msg = toError(error).message;
      this.view.announce("Không tải được: " + msg);
      this.hub?.toast("Không tải được: " + msg);
    } finally {
      this.downloading = false;
    }
  }

  /** Bản demo: đổi vai — hội thoại mới, gợi ý + quyền theo vai mới; báo trang chủ để đồng bộ. */
  switchRole(token: string): void {
    if (!token || token === this.demoToken) return;
    this.demoToken = token;
    this.emitter.emit("demo:role", { token });
    this.stop();
    this.session.reset();
    this.fresh = true;
    this.view.clearThread();
    this.remote = {};
    this.refreshIntro();
    if (this.config.fetchSession) void this.refreshSession();
    this.hub?.onRoleChanged();
  }

  /** Tải tệp người dùng chọn lên Hub ngay (tối đa 3/lượt); gửi câu hỏi thì kèm id. */
  private attachFiles(files: File[]): void {
    const OK: Record<string, number> = {
      "image/png": 5, "image/jpeg": 5, "image/webp": 5, "application/pdf": 10, "video/mp4": 25, "video/webm": 25,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": 15,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": 15,
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": 15,
    };
    for (const f of files) {
      if (this.pending.length >= 3) {
        this.hub?.toast("Tối đa 3 tệp mỗi câu hỏi");
        break;
      }
      const key = Math.random().toString(36).slice(2, 10);
      /* một số hệ điều hành để trống `type` của docx/xlsx/pptx/mp4 ⇒ suy theo đuôi tệp */
      const BY_EXT: Record<string, string> = { docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation", mp4: "video/mp4", webm: "video/webm", pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp" };
      const type = OK[f.type] ? f.type : BY_EXT[(f.name.split(".").pop() || "").toLowerCase()] || f.type;
      const limit = OK[type];
      if (!limit || f.size > limit * 1048576) {
        this.pending.push({ key, name: f.name, status: "error", error: limit ? "Quá " + limit + " MB" : "Chỉ nhận ảnh, PDF, Word/Excel/PowerPoint (docx/xlsx/pptx) hoặc video MP4/WEBM" });
        continue;
      }
      const item: PendingAttachment & { file?: Attachment } = { key, name: f.name, status: "uploading" };
      this.pending.push(item);
      this.transport
        .upload(type === f.type ? f : new Blob([f], { type }), f.name)
        .then((meta) => {
          item.status = "ready";
          item.file = meta;
        })
        .catch((error) => {
          item.status = "error";
          item.error = toError(error).message;
        })
        .finally(() => this.view.renderPending(this.pending));
    }
    this.view.renderPending(this.pending);
  }

  /** Thanh công cụ dưới câu trả lời. */
  private async messageTool(messageId: string, tool: "copy" | "save" | "up" | "down", feedback?: { category: FeedbackCategory; note?: string }): Promise<void> {
    const index = this.session.messages.findIndex((m) => m.id === messageId);
    const message = this.session.messages[index];
    if (!message) return;
    let question = "";
    for (let i = index - 1; i >= 0; i--) {
      const m = this.session.messages[i];
      if (m && m.role === "user") {
        question = m.text;
        break;
      }
    }
    try {
      if (tool === "copy") {
        await navigator.clipboard.writeText(message.text);
        this.view.markTool(messageId, "copy");
        this.view.announce("Đã sao chép");
        this.hub?.toast("Đã sao chép câu trả lời");
      } else if (tool === "save") {
        if (!question) throw new Error("Không tìm thấy câu hỏi của câu trả lời này");
        await this.transport.api("POST", "/jobs", { title: question.slice(0, 80), prompt: question, schedule: { kind: "none" }, result: { text: message.text, sources: message.sources || [] } });
        this.view.markTool(messageId, "save");
        this.view.announce("Đã lưu vào Báo cáo");
        this.hub?.toast("Đã lưu vào tab Báo cáo — có thể đặt lịch chạy lại");
      } else {
        /* 5.6: 👎 mang lý do người dùng chọn ở màn chọn lý do (+ ghi chú tuỳ chọn) */
        await this.transport.api("POST", "/feedback", feedbackBody({ value: tool, conversationId: this.session.conversationId, question, answer: message.text, ...feedback }));
        if (tool === "down") this.view.settleFeedback(messageId, true);
        this.view.markTool(messageId, tool);
        this.view.announce("Cảm ơn phản hồi của bạn");
        this.hub?.toast("Đã ghi nhận phản hồi");
      }
    } catch (error) {
      if (tool === "down") this.view.settleFeedback(messageId, false);
      this.view.announce(toError(error).message);
      this.hub?.toast("Không thực hiện được: " + toError(error).message);
    }
  }

  private async clearRemoteSession(): Promise<void> {
    const controller = new AbortController();
    this.sessionController = controller;
    this.busy = true;
    this.view.setBusy(true);
    try {
      await this.transport.clearSession(controller.signal);
    } catch (error) {
      if (controller.signal.aborted) return;
      const err = toError(error);
      if (err instanceof TransportError && err.isAuth) {
        this.emitter.emit("auth:error", { status: err.status ?? 401 });
      }
      this.view.announce(this.friendly(err));
      this.emitter.emit("error", { error: err, phase: "session" });
    } finally {
      if (this.sessionController === controller) this.sessionController = null;
      this.busy = false;
      this.view.setBusy(false);
      this.syncBlocked();
    }
  }

  getMessages(): ChatMessage[] {
    return this.session.messages.map((m) => ({ ...m }));
  }

  setContext(context: OpenContext | null): void {
    this.context = context;
  }

  setMetadata(metadata: Record<string, unknown>): void {
    this.config.metadata = metadata;
  }

  setTheme(theme: ThemeMode): void {
    this.config.theme = theme;
    this.explicit.add("theme");
    this.resolveTheme();
  }

  setLocale(locale: Locale): void {
    this.config.locale = locale;
    this.strings = strings(locale);
    this.view.setStrings(this.strings);
    this.voice.setLang(speechLang(locale));
    this.applyConfigToView();
    this.refreshIntro();
    this.syncBlocked();
  }

  private emitMessage(message: ChatMessage): void {
    this.emitter.emit("message", { message });
    this.config.onMessage?.(message);
  }

  update(options: Partial<WidgetOptions>): void {
    for (const key of Object.keys(options)) this.explicit.add(key);

    if (options.tokens) options.tokens = normalizeTokens(options.tokens);
    if (options.locale && options.locale !== this.config.locale) this.setLocale(options.locale);
    if (options.context !== undefined) this.context = options.context;

    assignDefined(this.config, options as Partial<ResolvedConfig>);
    if (options.endpoints) this.config.endpoints = { ...this.config.endpoints, ...options.endpoints };

    this.transport.update({
      apiBase: this.config.apiBase,
      chatPath: this.config.endpoints.chat,
      sessionPath: this.config.endpoints.session,
      headers: requestHeaders(this.config),
      credentials: this.config.credentials,
      timeout: Math.max(0, this.config.timeout),
      getToken: this.tokenSource,
      streamProtocol: this.config.streamProtocol,
      backend: this.config.backend,
    });

    if (options.persistSession !== undefined) this.session.setPersist(options.persistSession);
    if (options.voice !== undefined) this.view.setVoiceAvailable(options.voice && this.voice.supported);

    this.applyConfigToView();
    this.refreshIntro();
  }

  on<E extends WidgetEvent>(event: E, handler: (payload: WidgetEventMap[E]) => void): () => void {
    return this.emitter.on(event, handler);
  }

  off<E extends WidgetEvent>(event: E, handler: (payload: WidgetEventMap[E]) => void): void {
    this.emitter.off(event, handler);
  }

  get host(): HTMLElement {
    return this.view.host;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    this.stop();
    this.sessionController?.abort();
    if (this.autoOpenTimer) clearTimeout(this.autoOpenTimer);
    if (this.onKeydown) document.removeEventListener("keydown", this.onKeydown);
    if (this.themeQuery && this.onThemeChange && typeof this.themeQuery.removeEventListener === "function") {
      this.themeQuery.removeEventListener("change", this.onThemeChange);
    }
    this.voice.destroy();
    this.hub?.destroy();
    this.view.destroy();
    this.emitter.emit("destroy", undefined);
    this.emitter.clear();
  }

  /** Đánh dấu khoá app chủ khai từ đầu, để `applyRemote` chừa ra. */
  markExplicit(keys: string[]): void {
    for (const key of keys) this.explicit.add(key);
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) console.log("[ApusAssistant]", ...args);
  }

  private warn(message: string): void {
    console.warn("[ApusAssistant] " + message);
  }
}

/** Phần tử đang giữ focus. Bỏ qua <body> — đó là "không ai cả". */
function activeElement(): HTMLElement | null {
  const active = document.activeElement;
  if (!active || active === document.body || active === document.documentElement) return null;
  return active as HTMLElement;
}

function resolve(options: WidgetOptions): ResolvedConfig {
  const config: ResolvedConfig = {
    ...DEFAULTS,
    tokens: {},
    headers: {},
    endpoints: { ...DEFAULTS.endpoints },
  };

  assignDefined(config, options as Partial<ResolvedConfig>);

  config.endpoints = { ...DEFAULTS.endpoints, ...(options.endpoints || {}) };
  config.tokens = normalizeTokens(options.tokens);
  config.headers = { ...(options.headers || {}) };
  config.apiBase = (options.apiBase || "").replace(/\/+$/, "");
  config.historyLimit = clamp(options.historyLimit ?? DEFAULTS.historyLimit, 10, 1000);
  config.maxLength = clamp(options.maxLength ?? DEFAULTS.maxLength, 1, 8000);
  config.getToken = options.getToken ?? null;
  config.context = options.context ?? null;

  /* Cookie same-origin cần credentials; JWT thì không. Nếu app chủ không khai
     mà cũng không có getToken, giữ "same-origin" là lựa chọn an toàn nhất. */
  if (options.credentials) config.credentials = options.credentials;

  return config;
}

/** Tên cũ, giữ để không phá import sẵn có. */
export { Assistant as Widget };
